import React from 'react';
import SearchResults from '../SearchResults';
import LoadingIndicator from './LoadingIndicator';
import '../../styles/components/_search-results-enhanced.scss';

const SearchResultsContainer = ({
  isLoading,
  totalResults,
  selectedService,
  selectedArea,
  handleLoadMore,
  visibleResults,
  searchResults,
  isAiSearch = false,
  aiQuery = ''
}) => {
  // Calculate properties for display
  // Even if visible results array is empty but we have totalResults,
  // we should still show the correct total count for back navigation
  const displayCount = totalResults || 0;
  const hasResults = displayCount > 0;
  const displayHasMore = visibleResults && searchResults && 
                         visibleResults.length > 0 && 
                         visibleResults.length < searchResults.length;
  
  return (
    <div className="user-page__search-results-container">
      {isLoading ? (
        <LoadingIndicator />
      ) : (
        <SearchResults 
          totalMatches={displayCount}
          serviceType={selectedService?.label}
          location={selectedArea?.label}
          onLoadMore={handleLoadMore}
          hasMore={displayHasMore}
          lawyers={(() => {
            // Process results - server already handles deduplication
            const processedResults = visibleResults?.map(org => {
              // Check if this is an AI search result that's already properly formatted
              if (org.isAiResult) {
                // Clean up the service field to ensure only one practice area shows
                let cleanService = org.service;
                if (typeof cleanService === 'string' && cleanService.includes(',')) {
                  cleanService = cleanService.split(',')[0].trim();
                }
                if (!cleanService || cleanService.trim().length === 0) {
                  cleanService = 'General Practice';
                }
                
                // AI results are already in the correct format, just pass them through with enhanced data
                return {
                  id: org.id,
                  name: org.name,
                  firm: org.firm,
                  location: org.location,
                  rating: org.rating && org.rating > 0 ? org.rating : null,
                  reviews: org.reviews && org.reviews > 0 ? org.reviews : null,
                  service: cleanService, // Use cleaned service
                  isOnline: org.isOnline || false,
                  photo: org.photo,
                  isVerified: org.isVerified || org.is_verified || false,
                  teamPhoto: null,
                  slug: org.slug || `solicitor/${(org.name || '').toLowerCase().replace(/\s+/g, '-')}`,
                  // FIX: Map to correct organisation_id for reviews API
                  // AI search returns solicitor_id, but reviews API needs organisation_id
                  // The server enrichment should provide the correct organisation id
                  orgId: org.orgId || org.id, // This should be the organisation.id from enrichment
                  isAiResult: true,
                  needsReviewFetch: org.needsReviewFetch || false,
                  hasGooglePlacesData: org.hasGooglePlacesData || false,
                  google_review_count: org.google_review_count || org.google_reviews || null,
                  
                  // Enhanced AI search data
                  isEnriched: org.isEnriched,
                  total_reviews: org.total_reviews && org.total_reviews > 0 ? org.total_reviews : null,
                  google_reviews: org.google_reviews && org.google_reviews > 0 ? org.google_reviews : null,
                  zahoot_reviews: org.zahoot_reviews && org.zahoot_reviews > 0 ? org.zahoot_reviews : null,
                  google_rating: org.google_rating && org.google_rating > 0 ? org.google_rating : null,
                  available_slots: org.available_slots,
                  has_availability: org.has_availability,
                  recent_positive_review: org.recent_positive_review,
                  
                  // Contact and office information
                  phone: org.phone,
                  email: org.email,
                  website: org.website,
                  address: org.address,
                  postcode: org.postcode,
                  offices: org.offices,
                  
                  // Practice areas and organization details
                  practice_areas_detailed: org.practice_areas_detailed,
                  practice_areas: org.practice_areas,
                  is_claimed: org.is_claimed,
                  claim_status: org.claim_status,
                  team_photo: org.team_photo,
                  overview: org.overview,
                  accreditations: org.accreditations,
                  
                  // AI relevance
                  ai_relevance_score: org.ai_relevance_score
                };
              }
              
              // Use consistent structure for regular search results
              // AI search results are now pre-processed to match organization structure
              
              let service = 'General Practice';
              
              if (org.practice_areas && org.practice_areas.length > 0) {
                if (selectedService?.label) {
                  // Look for exact match with selectedService first
                  const matchingArea = org.practice_areas.find(
                    pa => pa.name?.toLowerCase() === selectedService?.label?.toLowerCase()
                  );
                  if (matchingArea) {
                    service = matchingArea.name;
                  } else {
                    // Default to first area if no match found - ensure it's just one
                    const firstArea = org.practice_areas[0];
                    service = typeof firstArea === 'object' ? firstArea.name : firstArea;
                  }
                } else {
                  // No selectedService, use ONLY the first area
                  const firstArea = org.practice_areas[0];
                  service = typeof firstArea === 'object' ? firstArea.name : firstArea;
                }
              } else if (org.service) {
                // Fallback to service field for AI results - ensure it's clean
                service = org.service;
              }
              
              // Ensure service is clean, not empty, and is a single practice area
              if (!service || service.trim().length === 0) {
                service = 'General Practice';
              }
              
              // Handle cases where service might be comma-separated multiple areas
              if (typeof service === 'string' && service.includes(',')) {
                service = service.split(',')[0].trim(); // Take only the first one
              }
              
              return {
                id: org.id,
                name: org.practice_name || org.name,
                firm: org.practice_name || org.name,
                location: (() => {
                  if (!org.offices) return org.location || 'Location not provided';
                  
                  // If searching for specific area, show that office first
                  if (selectedArea?.label) {
                    const matchingOffice = org.offices.find(office => 
                      office.town?.toLowerCase() === selectedArea.label.toLowerCase()
                    );
                    if (matchingOffice) return matchingOffice.town;
                  }
                  
                  // Fallback to first office
                  return org.offices[0]?.town || 'Location not provided';
                })(),
                rating: (() => {
                  const rating = org.google_rating || org.rating;
                  return rating && rating > 0 ? rating : null;
                })(),
                reviews: (() => {
                  const reviewCount = org.google_review_count || org.reviews_count;
                  return reviewCount && reviewCount > 0 ? reviewCount : null;
                })(),
                service: service,
                isOnline: false,
                photo: null,
                isVerified: Boolean(org.is_verified),
                teamPhoto: org.team_photo || null,
                slug: org.slug || `solicitor/${(org.practice_name || org.name || '').toLowerCase().replace(/\s+/g, '-')}`,
                orgId: org.id,
                
                // Enhanced data for regular results too
                total_reviews: (() => {
                  const total = org.review_count || org.google_review_count;
                  return total && total > 0 ? total : null;
                })(),
                google_reviews: org.google_review_count && org.google_review_count > 0 ? org.google_review_count : null,
                google_rating: org.google_rating && org.google_rating > 0 ? org.google_rating : null,
                available_slots: 0, // Regular results don't have this data yet
                offices: org.offices || [],
                practice_areas_detailed: org.practice_areas || [],
                practice_areas: org.practice_areas || []
              };
            }) || [];

            // No client-side deduplication needed
            return processedResults;
          })()}
          isAiSearch={isAiSearch}
          aiQuery={aiQuery}
        />
      )}
    </div>
  );
};

export default SearchResultsContainer; 